// frontend/pages/POS.jsx
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth'; 
import ProductGrid from '../components/POS/ProductGrid';
import Cart from '../components/POS/Cart';
import CheckoutModal from '../components/POS/CheckoutModal';
import { transactionService } from '../services/api';
import { toast } from 'react-hot-toast';
import { ShoppingCartIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { mpesaService } from '../services/mpesa';

export default function POS() {
  const { user } = useAuth(); 
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleCheckout = (e) => {
      setCheckoutData({
        total: e.detail?.total || 0,
        items: e.detail?.items || []
      });
      setShowCheckout(true);
    };

    window.addEventListener('pos-checkout', handleCheckout);
    return () => window.removeEventListener('pos-checkout', handleCheckout);
  }, []);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, product];
    });
    toast.success(`${product.name} added to cart`);
    
    if (window.innerWidth < 1024) {
      setShowMobileCart(true);
    }
  };

  const updateQuantity = (index, quantity) => {
    if (quantity <= 0) {
      removeItem(index);
      return;
    }
    setCart(prev => prev.map((item, i) => i === index ? { ...item, quantity } : item));
  };

  const removeItem = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const clearCart = () => {
    setCart([]);
    setShowMobileCart(false);
  };

  // frontend/pages/POS.jsx - Updated handlePaymentComplete with M-Pesa support

// frontend/pages/POS.jsx - Updated handlePaymentComplete (No Polling)

const handlePaymentComplete = async (paymentData) => {
  if (isProcessing) {
    return { success: false };
  }
  
  setIsProcessing(true);
  
  try {
    // For M-Pesa payments - No Polling, rely on webhook callback
    if (paymentData.method === 'mpesa') {
      // Show phone number prompt
      const phoneNumber = prompt('Enter M-Pesa phone number (e.g., 0712345678):');
      if (!phoneNumber) {
        toast.error('Phone number required for M-Pesa payment');
        setIsProcessing(false);
        return { success: false };
      }
      
      // Validate phone number format
      const phoneRegex = /^(07|01|02)[0-9]{8}$/;
      if (!phoneRegex.test(phoneNumber)) {
        toast.error('Invalid phone number format. Use 07XXXXXXXX');
        setIsProcessing(false);
        return { success: false };
      }
      
      // Create the sale with pending payment status
      const saleData = {
        items: cart.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          unitPrice: item.price,
          taxRate: item.tax_rate,
          description: item.name
        })),
        paymentMethod: 'mpesa',
        amountPaid: 0, // Not paid yet - will be updated by webhook
        status: 'pending', // Mark as pending payment
        customerId: paymentData.customerId || null,
        cashierNotes: paymentData.notes
      };

      const response = await transactionService.createPOSSale(saleData);
      
      // Initiate M-Pesa STK Push
      const mpesaResponse = await mpesaService.initiateSTKPush({
        phoneNumber,
        amount: paymentData.amount,
        invoiceNumber: response.invoiceNumber,
        transactionId: response.transactionId
      });
      
      if (!mpesaResponse.success) {
        toast.error(mpesaResponse.message || 'Failed to initiate M-Pesa payment');
        setIsProcessing(false);
        return { success: false };
      }
      
      // Clear cart and close modal immediately
      setCart([]);
      setShowCheckout(false);
      setShowMobileCart(false);
      
      // Show success message
      toast.success('STK Push sent! Check your phone to complete payment.');
      toast('Payment pending. You will receive confirmation shortly.', { duration: 5000, icon: '⏳' });
      
      setIsProcessing(false);
      
      // Return success - payment will be updated via webhook callback
      return {
        success: true,
        pending: true,
        transactionId: response.transactionId,
        invoiceNumber: response.invoiceNumber,
        message: 'Payment pending. You will receive confirmation shortly.'
      };
    }
    
    // For non-M-Pesa payments (cash, card, credit)
    const saleData = {
      items: cart.map(item => ({
        productId: item.id,
        quantity: item.quantity,
        unitPrice: item.price,
        taxRate: item.tax_rate,
        description: item.name
      })),
      paymentMethod: paymentData.method,
      amountPaid: paymentData.amount,
      customerId: paymentData.customerId || null,
      cashierNotes: paymentData.notes
    };

    const response = await transactionService.createPOSSale(saleData);
    
    setCart([]);
    setShowCheckout(false);
    setShowMobileCart(false);
    queryClient.invalidateQueries(['products']);
    
    toast.success(`Sale completed! Invoice: ${response.invoiceNumber}`);
    
    if (paymentData.printReceipt) {
      setTimeout(() => {
        window.open(`/receipt/${response.transactionId}`, '_blank');
      }, 500);
    }
    
    setIsProcessing(false);
    
    return {
      success: true,
      transactionId: response.transactionId,
      invoiceNumber: response.invoiceNumber,
      cashierName: user?.full_name,
      customerName: paymentData.customerName,
      total: cart.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      amountPaid: paymentData.amount,
      paymentMethod: paymentData.method,
      items: cart
    };
    
  } catch (error) {
    console.error('Payment error:', error);
    toast.error(error.response?.data?.error || 'Failed to process sale');
    setIsProcessing(false);
    return { success: false };
  }
};

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Mobile Header with Cart Button */}
      <div className="lg:hidden bg-white shadow-sm px-4 py-3 flex justify-between items-center sticky top-0 z-20">
        <h1 className="text-lg font-semibold text-gray-900">Point of Sale</h1>
        <button
          onClick={() => setShowMobileCart(true)}
          className="relative p-2 rounded-full bg-blue-600 text-white"
        >
          <ShoppingCartIcon className="h-5 w-5" />
          {cartItemCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {cartItemCount}
            </span>
          )}
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Products Section */}
        <div className={`
          flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 transition-all duration-300
          ${showMobileCart && 'hidden lg:block'}
        `}>
          <ProductGrid onAddToCart={addToCart} />
        </div>

        {/* Cart Section - Desktop */}
        <div className="hidden lg:flex lg:w-96 bg-white border-l border-gray-200 flex-col">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Current Sale</h2>
            <p className="text-sm text-gray-500">{cartItemCount} item(s)</p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <Cart
              items={cart}
              onUpdateQuantity={updateQuantity}
              onRemoveItem={removeItem}
              onClearCart={clearCart}
            />
          </div>
        </div>

        {/* Mobile Cart Drawer */}
        <div className={`
          fixed inset-0 z-50 lg:hidden transition-transform duration-300 ease-in-out
          ${showMobileCart ? 'translate-x-0' : 'translate-x-full'}
        `}>
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileCart(false)}
          />
          
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold">Current Sale</h2>
                <p className="text-sm text-gray-500">{cartItemCount} item(s)</p>
              </div>
              <button
                onClick={() => setShowMobileCart(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <Cart
                items={cart}
                onUpdateQuantity={updateQuantity}
                onRemoveItem={removeItem}
                onClearCart={() => {
                  clearCart();
                  setShowMobileCart(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Floating Cart Button */}
      {cartItemCount > 0 && !showMobileCart && (
        <div className="lg:hidden fixed bottom-20 right-4 z-30">
          <button
            onClick={() => setShowMobileCart(true)}
            className="bg-blue-600 text-white rounded-full p-4 shadow-lg flex items-center justify-center space-x-2 animate-bounce"
          >
            <ShoppingCartIcon className="h-6 w-6" />
            <span className="font-semibold">KES {cartTotal.toLocaleString()}</span>
          </button>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          total={checkoutData?.total}
          items={checkoutData?.items}
          onClose={() => setShowCheckout(false)}
          onConfirm={handlePaymentComplete}
        />
      )}
    </div>
  );
}